import store from '@/store'; // Adjust the path to your Vuex store as necessary
import _ from 'lodash'; // Make sure lodash is imported if you're using it

export function authMiddleware(to, from, next) {
  const publicRoutes = ['/login', '/customer', '/customer/forgot', '/customer/reset-password', '/customer/invitation', '/old-browser', '/maintenance', '/basket', '/punchout-only', '/browser-only', '/punchout-login-assistance'];
  const authFlowRoutes = ['/customer/forgot', '/customer/reset-password', '/customer/invitation'];
  const loggedIn = _.get(store, 'state.account.loggedIn');
  const isAdminPath = to.path.startsWith('/admin');
  // Use the store getter so the fallback to adminRole (new backend contract)
  // is honoured for admins who only have the new admin_role_id set.
  const isEvolveUser = _.get(store, 'getters', {})['account/isEvolveUser'];
  const permissions = _.get(store, 'state.account.session.account.permissions', []);
  const adminPermissions = _.get(store, 'state.account.session.account.adminPermissions', []);
  // The backend knows what kind of session the token is for. The in-memory flag is
  // lost on a page reload, so it is only the fallback when the session doesn't say.
  const sessionPunchoutFlag = _.get(store, 'state.account.session.setting.punchoutSession');
  const isPunchoutSession = _.isBoolean(sessionPunchoutFlag)
    ? sessionPunchoutFlag
    : _.get(store, 'state.account.isPunchoutSession');
  const punchoutOnly = !isPunchoutSession && permissions.includes('punchoutOnly');
  const browserOnly = isPunchoutSession && permissions.includes('browserOnly');
  // The backend lists only the entities usable in this kind of session. When the
  // logged-in contact is not usable but another linked entity is, the user is sent
  // to the entity selection rather than the unavailable page.
  const hasEligibleEntity = _.get(store, 'state.account.session.entityAccess.hasEligibleEntity', false);

  // Auth-flow routes are reachable regardless of session state so punchout-only
  // users with a stale session can still complete password reset / invitation / forgot.
  // Other public routes (basket, maintenance, broad /customer, etc.) are intentionally
  // left in the publicRoutes block below — logged-in punchout-only users hitting those
  // should still be redirected to /punchout-only by the if (loggedIn) block.
  if (authFlowRoutes.some(path => to.path.startsWith(path))) {
    return next();
  }

  // If the user is logged in
  if (loggedIn) {
    // Check if user is punchout-only and not in a punchout session
    // Don't redirect from login page to allow punchout session establishment
    if (punchoutOnly && to.path !== '/punchout-only' && to.path !== '/login') {
      if (hasEligibleEntity) {
        return to.path === '/select-your-customer' ? next() : next('/select-your-customer');
      }
      return next('/punchout-only');
    }

    // Same the other way round: a browser-only contact inside a Punchout session
    if (browserOnly && to.path !== '/browser-only' && to.path !== '/login') {
      if (hasEligibleEntity) {
        return to.path === '/select-your-customer' ? next() : next('/select-your-customer');
      }
      return next('/browser-only');
    }

    // First gate: non-admin users can't access /admin/* at all.
    if (!isEvolveUser && isAdminPath) {
      return next('/');
    }

    // Second gate: admin routes with a meta.permission key require that
    // specific admin permission key in the user's adminPermissions array.
    // meta.permission may also be an array, for a page reachable via any
    // one of several permissions (e.g. a page with permission-gated tabs).
    if (isAdminPath) {
      const requiredPermission = _.get(to, 'meta.permission');
      const hasRequiredPermission = Array.isArray(requiredPermission)
        ? requiredPermission.some(p => _.includes(adminPermissions, p))
        : _.includes(adminPermissions, requiredPermission);
      if (requiredPermission && !hasRequiredPermission) {
        return next('/');
      }
    }

    // Permission mapping
    const mapping = [
      { path: '/manpack', permission: 'canViewManpack' },
      { path: '/wearer-pack', permission: 'canViewManpack' },
      { path: '/catalogue', permission: 'canViewFullCatalogue' },
      { path: '/my-catalogue', permission: 'canViewMyCatalogue' },
      { path: '/employee-catalogue', permission: 'canViewUserCatalogue' },
      { path: '/order-history', permission: 'viewOrderHistory' },
      { path: '/order-to-approve', permission: 'orderApproval' },
      { path: '/report', permission: 'viewReporting' },
      { path: '/new-manpack-user', permission: 'addManpackUser' },
      { path: '/add-site-user', permission: 'addSiteUser' },
    ];

    // Check if the user has the required permissions for the requested path
    for (let map of mapping) {
      if (to.path.startsWith(map.path) && !permissions.includes(map.permission)) {
        return next('/'); // Redirect to homepage if no permission
      }
    }
  }

  // Allow access to public routes if the route starts with any public route path
  if (publicRoutes.some(path => to.path.startsWith(path))) {
    return next(); // Allow access to public routes
  }

  // If the user is not logged in and trying to access a restricted route
  if (!loggedIn) {
    return next(`/login?redirect=${encodeURIComponent(to.fullPath)}`); // Redirect to login with redirect query
  }

  // Proceed to the requested route
  next();
}
